import * as React from 'react';
import renderIcon from 'helpers/renderIcon';
import { SearchInput } from 'components/Search/SearchInput';
import { EllipseGreyBlackButton } from '../Button';
import memoize from 'memoize-one';
import { PortalOneColumnLayout } from 'components/PortalStructure';
require('./style.scss');

interface IColumn<TableItem> {
	title: string;
	/**
	 * bootstrap size: col-<size>
	 */
	size: number;
	/**
	 * Determines what data to display from each element. String for prop name of element, function for a custom getter
	 */
	getter: ((order: TableItem, index: number) => string) | string;
	className?: string;
	/**
	 * can user sort by this column
	 */
	sortable?: boolean;
	/**
	 * how to sort items if column is sortable
	 */
	compareFnc?: (order1: TableItem, order2: TableItem) => number;
	defaultReversed?: boolean;
}

interface IProps<TableItem> {
	areThereMoreElementsToLoad: () => any;
	handleItemClicked: (order: TableItem) => any;
	handleViewMoreClick: () => any;
	handleSearchValueChange: (e: any) => void;
	handleClearSearchInput: () => void;
	filterFunction: (item: TableItem, keyword: string) => boolean;
	viewMoreButtonLabel: string;
	totalItemsLabel?: string | React.ReactElement;
	searchPlaceHolder: string;
	keywordFilter: string;
	items: TableItem[];
	columns: IColumn<TableItem>[];
	noItemsDescription?: string;
	noItemsTitle?: string;
}

interface IState {
	searchOpen: boolean;
	sortBy: number;
	reversed: boolean;
}

const reverseCmp = (cmpFnc) => (item1, item2) => cmpFnc(item2, item1);

export class SearchableTable<TableItem> extends React.Component<IProps<TableItem>, IState> {
	constructor(props) {
		super(props);

		this.handleSearchToggle = this.handleSearchToggle.bind(this);
		this.handleClearSearchInput = this.handleClearSearchInput.bind(this);

		this.state = {
			searchOpen: false,
			sortBy: -1,
			reversed: false,
		};
	}

	// ts sometimes throws error memoise-one in not a function for some reason
	sortItems = (() => {
		const fnc = (items, sortFnc, reversed) => {
			if (sortFnc) {
				return [...items].sort(reversed ? reverseCmp(sortFnc) : sortFnc);
			} else {
				return items;
			}
		};
		if (typeof memoize == 'function') {
			return memoize(fnc);
		} else {
			return fnc;
		}
	})();

	componentDidMount() {
		this.props.columns.some((column, i) => {
			if (column.sortable) {
				this.setState({ sortBy: i, reversed: column?.defaultReversed });
				return true;
			}
			return false;
		});
	}

	componentDidUpdate(oldProps) {
		if (!oldProps.columns && this.props.columns)
			this.props.columns.some((column, i) =>
				column.sortable ? true || this.setState({ sortBy: i, reversed: column?.defaultReversed }) : false,
			);
	}

	private handleViewMoreClick = () => {
		this.props.handleViewMoreClick();
	};

	private handleSearchToggle() {
		this.setState((prev) => ({ searchOpen: !prev.searchOpen }));
	}

	private handleClearSearchInput() {
		this.setState({ searchOpen: false });
		this.props.handleClearSearchInput();
	}

	static getDerivedStateFromProps(props, state) {
		return {
			sortedItems: state.sortBy != -1 ? [...props.items].sort(props.columns[state.sortBy].compareFnc) : props.items,
		};
	}

	render() {
		const {
			areThereMoreElementsToLoad,
			handleItemClicked,
			keywordFilter,
			items,
			columns,
			viewMoreButtonLabel,
			totalItemsLabel,
			handleSearchValueChange,
			searchPlaceHolder,
			noItemsDescription,
			noItemsTitle,
		} = this.props;
		const sortedItems = this.sortItems(
			items,
			this.props?.columns?.[this.state.sortBy]?.compareFnc,
			this.state.reversed,
		);

		if (!items?.length) {
			return (
				<div className="container no-items">
					<h3>{noItemsTitle}</h3>
					<p>{noItemsDescription}</p>
				</div>
			);
		}

		const renderElements = (header?: boolean): JSX.Element | JSX.Element[] | null => {
			const usingFilter = keywordFilter;
			if (header) {
				return (
					<div className="header row">
						{columns.map((column, index) => (
							<div
								onClick={() => column.sortable && this.setState((prevState) => ({ reversed: !prevState.reversed }))}
								className={`header-item col-${column.size}${column.className ? ' ' + column.className : ''}${
									column.sortable ? ' sortable' : ''
								}`}
								key={`column_${index}`}
							>
								<p className="bold">
									{column.title.toUpperCase()}
									{column.sortable && (
										<span className={`sort-icon${this.state.reversed ? ' reversed' : ''}`}>
											{renderIcon('dropdown')}
										</span>
									)}
								</p>
							</div>
						))}
					</div>
				);
			} else if (items.length) {
				// To preserve indexes
				const out: any = sortedItems.map((item, index) => {
					const elem = (
						<div className="body row pointer" key={index} onClick={() => handleItemClicked(item)}>
							{columns.map((column, idx2) => {
								let content = '';
								if (typeof column.getter === 'string') {
									content = item[column.getter as string];
								} else if (column.getter) {
									content = (column.getter as (item: TableItem, index: number) => string)(item, index);
								}
								return (
									<div
										className={`col-${column.size}${column.className ? ' ' + column.className : ''}`}
										key={`${index}${idx2}`}
									>
										<p>{content}</p>
									</div>
								);
							})}
							<div className={`fade-element ${areThereMoreElementsToLoad() ? 'show' : ''}`} />
						</div>
					);
					if (usingFilter) {
						return { item, elem };
					} else {
						return elem;
					}
				});

				if (usingFilter) {
					return out
						.filter((item) => {
							if (this.props.filterFunction) {
								return this.props.filterFunction(item.item, usingFilter);
							} else {
								return true;
							}
						})
						.map((el) => el.elem);
				} else {
					return out;
				}
			}
			return null;
		};

		return (
			<PortalOneColumnLayout className="container searchableTable">
				<div className="row top-header d-flex align-items-center">
					<div className="col-3 d-flex align-items-center">
						<p className="p12 follow-count">{totalItemsLabel}</p>
					</div>
					<div className="col-9 d-flex justify-content-end">
						{!this.state.searchOpen ? (
							renderIcon('search3', 40, 40, 'searchIcon pointer', this.handleSearchToggle)
						) : (
							<SearchInput
								keyword={keywordFilter}
								onSearchValueChange={handleSearchValueChange}
								onClearSearchInput={this.handleClearSearchInput}
								placeholder={searchPlaceHolder}
								customIconSizeHeight={32}
								customIconSizeWidth={32}
							/>
						)}
					</div>
				</div>
				<div className="searchableTableItems">
					{renderElements(true)}
					{renderElements()}
					{areThereMoreElementsToLoad() ? (
						<EllipseGreyBlackButton className="view-more-btn" onClick={this.handleViewMoreClick}>
							{viewMoreButtonLabel}
						</EllipseGreyBlackButton>
					) : null}
				</div>
			</PortalOneColumnLayout>
		);
	}
}
